/**
 * Green Flag — Button recipe
 *
 * Five variants, each mapped to a product surface:
 *   - solid      → primary CTA ("Sign Up", "Chat with Match", "See Who Likes You")
 *   - subtle     → secondary filler (gray pill)
 *   - outline    → Dating outline (green border on white)
 *   - friends    → Friends outline (blue border on white)
 *   - friendSolid → Friends solid ("Chat with Friend")
 *
 * The pill radius is non-negotiable brand DNA — every button in the system
 * is rounded-full. There is no square button variant.
 */

import { defineRecipe } from "@chakra-ui/react";

export const buttonRecipe = defineRecipe({
  className: "gf-btn",

  base: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "2",
    borderRadius: "pill",
    fontFamily: "body",
    fontWeight: "bold",
    lineHeight: "tight",
    minHeight: "var(--button-height, 2.5rem)",
    cursor: "pointer",
    userSelect: "none",
    transitionProperty: "filter, transform, background-color, color, border-color",
    transitionDuration: "normal",
    transitionTimingFunction: "standard",
    border: "0",
    _hover: { filter: "brightness(1.04)" },
    _active: { transform: "translateY(0.5px)", filter: "brightness(0.95)" },
    _focusVisible: { outline: "none", boxShadow: "focusRing" },
    _disabled: { opacity: 0.4, cursor: "not-allowed", _hover: { filter: "none" } },
  },

  variants: {
    variant: {
      solid: {
        bg: "bg.brand",
        color: "fg.onBrand",
      },
      subtle: {
        bg: "bg.subtle",
        color: "fg.default",
      },
      outline: {
        bg: "transparent",
        color: "fg.brand",
        boxShadow: "inset 0 0 0 1px var(--chakra-colors-border-brand)",
      },
      friends: {
        bg: "transparent",
        color: "fg.friends",
        boxShadow: "inset 0 0 0 1px var(--chakra-colors-border-friends)",
      },
      friendSolid: {
        bg: "bg.friends",
        color: "fg.onBrand",
      },
      ghost: {
        bg: "transparent",
        color: "fg.default",
      },
    },
    size: {
      sm: {
        h: "7",              // 28px
        px: "4",
        fontSize: "md",
      },
      md: {
        h: "33px",           // 33px — Figma primary CTA
        px: "6",              // 24px horizontal
        fontSize: "md",
      },
      lg: {
        h: "38px",           // 38px — Sign Up on Game Night card
        px: "6",
        fontSize: "md",
      },
      xl: {
        h: "10",             // 40px
        px: "8",
        fontSize: "lg",
      },
      "2xl": {
        h: "54px",           // 54px — registration / sheet primary CTA
        px: "28px",
        fontSize: "lg",
      },
    },
  },

  defaultVariants: {
    variant: "solid",
    size: "md",
  },
});
